import { Link, createFileRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import {
  CheckCircle2,
  Loader2,
  MapPin,
  RefreshCw,
  Snowflake,
  StopCircle,
  TrendingDown,
  X,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { LoadingSpinner } from '@/components/loading-spinner'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { useSnowHuntStatus, useStopSnowHunt } from '@/lib/api'
import { useAccounts } from '@/lib/use-accounts'
import { localizeDigits } from '@/lib/localize-digits'

export const Route = createFileRoute('/snow-hunt_/status')({
  component: SnowHuntStatusPage,
})

function SnowHuntStatusPage() {
  const { t } = useTranslation()
  const { accounts: accountsList, isLoading: accountsLoading } = useAccounts()
  const stopSnowHuntMutation = useStopSnowHunt()

  // Use first account as primary to poll status
  // We need to know the primary account to query status
  const primaryAccount =
    accountsList.length > 0 ? accountsList[0].id : undefined

  const {
    data: huntStatus,
    isLoading,
    isError,
    refetch,
  } = useSnowHuntStatus(primaryAccount, true)

  // If status has a primary_account, use that instead
  const effectivePrimary = huntStatus?.params.primary_account ?? primaryAccount

  // Re-query with correct primary if different
  const { data: correctStatus } = useSnowHuntStatus(
    effectivePrimary !== primaryAccount ? effectivePrimary : undefined,
    effectivePrimary !== primaryAccount,
  )

  const status = correctStatus ?? huntStatus

  const [countdown, setCountdown] = useState(5)

  useEffect(() => {
    const interval = setInterval(() => {
      setCountdown((prev) => (prev <= 1 ? 5 : prev - 1))
    }, 1000)
    return () => clearInterval(interval)
  }, [])

  const handleStop = async () => {
    if (!status?.params.primary_account) return
    try {
      await stopSnowHuntMutation.mutateAsync({
        primaryAccount: status.params.primary_account,
      })
      toast.success(t('snowHunt.stopped'))
    } catch (error) {
      console.error('Failed to stop snow hunt:', error)
      toast.error(t('snowHunt.stopFailed'))
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return localizeDigits(
      new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      }).format(date),
    )
  }

  const getStatusBadge = (statusStr: string) => {
    const variants: Record<string, 'default' | 'secondary' | 'destructive'> = {
      searching: 'default',
      booked: 'default',
      optimizing: 'default',
      stopped: 'secondary',
      error: 'destructive',
    }
    const variant = variants[statusStr] ?? 'secondary'
    const isActive = statusStr === 'searching' || statusStr === 'optimizing'

    return (
      <Badge variant={variant} className={isActive ? 'animate-pulse' : ''}>
        {isActive && <Loader2 className="h-3 w-3 me-1 animate-spin" />}
        {t(`snowHunt.status.${statusStr}`) || statusStr}
      </Badge>
    )
  }

  if (accountsLoading || isLoading) {
    return (
      <div className="fixed-fullscreen-safe fixed overflow-y-auto">
        <div className="container mx-auto max-w-screen-md px-4 py-4">
          <Card>
            <CardContent className="p-8">
              <LoadingSpinner size="lg" />
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  if (isError || !status) {
    return (
      <div className="fixed-fullscreen-safe fixed overflow-y-auto">
        <div className="container mx-auto max-w-screen-md px-4 py-4">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-bold">
              <Snowflake className="h-6 w-6 inline me-2" />
              {t('snowHunt.statusTitle')}
            </h1>
          </div>
          <Card>
            <CardContent className="p-8 text-center">
              <Snowflake className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground mb-4">
                {t('snowHunt.noActiveHunt')}
              </p>
              <Link to="/snow-hunt">
                <Button>
                  <Snowflake className="h-4 w-4 me-2" />
                  {t('snowHunt.startNew')}
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  const isActive =
    status.status === 'searching' || status.status === 'optimizing'

  return (
    <div className="fixed-fullscreen-safe fixed overflow-y-auto">
      <div className="container mx-auto max-w-screen-md px-4 py-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Snowflake className="h-6 w-6" />
              {t('snowHunt.statusTitle')}
            </h1>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => refetch()} size="icon" variant="outline">
              <div className="flex flex-col items-center justify-center">
                <span className="text-xs font-bold">
                  {localizeDigits(String(countdown))}
                </span>
                <span className="text-[8px] leading-none">sec</span>
              </div>
            </Button>
            <Link to="/snow-hunt">
              <Button variant="outline" size="icon">
                <Snowflake className="h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>

        {/* Overall Status */}
        <Card className="mb-3">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">
                  {t('snowHunt.overallStatus')}
                </span>
                {getStatusBadge(status.status)}
              </div>
              <span className="text-xs text-muted-foreground">
                {t('search.startedAt')}: {formatDate(status.start_time)}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Per-Account Status Cards */}
        {status.account_statuses && status.account_statuses.length > 0 && (
          <div className="space-y-2 mb-3">
            <h2 className="text-sm font-medium text-muted-foreground">
              {t('snowHunt.accountStatuses')}
            </h2>
            {status.account_statuses.map((acc) => (
              <Card key={acc.account_name}>
                <CardContent className="p-3">
                  <div className="flex items-center justify-between mb-2">
                    <CardTitle className="text-sm">
                      {acc.account_name}
                    </CardTitle>
                    <Badge
                      variant={
                        acc.status === 'searching'
                          ? 'default'
                          : acc.status === 'booked'
                            ? 'default'
                            : acc.status === 'error'
                              ? 'destructive'
                              : 'secondary'
                      }
                      className="text-xs"
                    >
                      {acc.status === 'searching' && (
                        <Loader2 className="h-3 w-3 me-1 animate-spin" />
                      )}
                      {acc.status}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div>
                      <span className="text-muted-foreground">
                        {t('search.evaluated')}:
                      </span>
                      <span className="font-medium ms-1">
                        {localizeDigits(String(acc.vehicles_evaluated))}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">
                        {t('search.attempts')}:
                      </span>
                      <span className="font-medium ms-1">
                        {localizeDigits(String(acc.booking_attempts))}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">
                        {t('snowHunt.lastUseRejections')}:
                      </span>
                      <span className="font-medium ms-1">
                        {localizeDigits(String(acc.last_use_rejections))}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">
                        {t('search.requests')}:
                      </span>
                      <span className="font-medium ms-1">
                        {localizeDigits(String(acc.upstream_requests))}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">
                        {t('search.errors')}:
                      </span>
                      <span className="font-medium ms-1">
                        {localizeDigits(String(acc.upstream_errors))}
                      </span>
                    </div>
                  </div>
                  {acc.last_error && (
                    <div className="mt-2 p-2 bg-destructive/10 rounded text-xs text-destructive">
                      {acc.last_error}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Booking Result */}
        {status.booking && (
          <Card className="mb-3 border-green-500/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                <span className="font-medium">{t('snowHunt.carBooked')}</span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-muted-foreground">
                    {t('snowHunt.bookedBy')}:
                  </span>
                  <span className="font-medium ms-1">
                    {status.booking.account_name}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">
                    {t('carsMap.model')}:
                  </span>
                  <span className="font-medium ms-1">
                    {status.booking.model || `#${status.booking.vehicle_nb}`}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">
                    {t('snowHunt.vehicleNumber')}:
                  </span>
                  <span className="font-medium ms-1">
                    {localizeDigits(String(status.booking.vehicle_nb))}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">
                    {t('search.rentalId')}:
                  </span>
                  <span className="font-medium ms-1">
                    {localizeDigits(String(status.booking.rental_id))}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground">
                <MapPin className="h-3 w-3" />
                <span>
                  {localizeDigits(status.booking.latitude.toFixed(5))},{' '}
                  {localizeDigits(status.booking.longitude.toFixed(5))}
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {t('snowHunt.bookedAt')}: {formatDate(status.booking.booked_at)}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Optimization Status */}
        {status.optimization && (
          <Card className="mb-3">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <TrendingDown className="h-4 w-4" />
                <span className="font-medium text-sm">
                  {t('snowHunt.optimization')}
                </span>
                <Badge
                  variant={
                    status.optimization.status === 'running'
                      ? 'default'
                      : 'secondary'
                  }
                  className="text-xs"
                >
                  {status.optimization.status === 'running' && (
                    <Loader2 className="h-3 w-3 me-1 animate-spin" />
                  )}
                  {status.optimization.status}
                </Badge>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <span className="text-muted-foreground">
                    {t('search.evaluated')}:
                  </span>
                  <span className="font-medium ms-1">
                    {localizeDigits(
                      String(status.optimization.vehicles_evaluated),
                    )}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">
                    {t('snowHunt.swapCount')}:
                  </span>
                  <span className="font-medium ms-1">
                    {localizeDigits(String(status.optimization.swap_count))}
                  </span>
                </div>
                {status.optimization.current_distance_km != null && (
                  <div>
                    <span className="text-muted-foreground">
                      {t('snowHunt.currentDistance')}:
                    </span>
                    <span className="font-medium ms-1">
                      {localizeDigits(
                        status.optimization.current_distance_km.toFixed(2),
                      )}{' '}
                      km
                    </span>
                  </div>
                )}
                {status.optimization.best_candidate_distance_km != null && (
                  <div>
                    <span className="text-muted-foreground">
                      {t('snowHunt.bestDistance')}:
                    </span>
                    <span className="font-medium ms-1">
                      {localizeDigits(
                        status.optimization.best_candidate_distance_km.toFixed(
                          2,
                        ),
                      )}{' '}
                      km
                    </span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stop Button */}
        {isActive && (
          <Button
            className="w-full"
            variant="destructive"
            size="lg"
            onClick={handleStop}
            disabled={stopSnowHuntMutation.isPending}
          >
            {stopSnowHuntMutation.isPending ? (
              <Loader2 className="h-5 w-5 me-2 animate-spin" />
            ) : (
              <StopCircle className="h-5 w-5 me-2" />
            )}
            {t('snowHunt.stopHunt')}
          </Button>
        )}

        {/* Start New Hunt when stopped */}
        {!isActive && (
          <Link to="/snow-hunt" className="block">
            <Button className="w-full" size="lg">
              <Snowflake className="h-5 w-5 me-2" />
              {t('snowHunt.startNew')}
            </Button>
          </Link>
        )}
      </div>
    </div>
  )
}
